import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  PortalComment,
  PortalContext,
  PortalDecisionResult,
  PortalMonth,
  PortalReviewDecision,
} from "@/types/clientPortal.type";
import { clientBriefsKey, markBriefDecided, type PortalSource } from "./clientPortalApi";
import { POLL_PORTAL_MS } from "@/lib/realtime";
import { livePoll, useCalendarLive } from "@/app/routes/dashboard/content-calendar/api/calendarLive";
import { scrubVendorTermsDeep } from "@/lib/vendorTerms";

// Everything is keyed by the token alone: a token IS one month, so there is no
// year/month to key on and no way to ask for a different one.
const contextKey = (token: string) => ["clientPortal", "context", token];
const monthKey = (token: string) => ["clientPortal", "month", token];

export function useClientContext(source: PortalSource) {
  const token = source.id;
  return useQuery({
    queryKey: contextKey(token),
    queryFn: async () => {
      const res = await source.get<PortalContext>("context");
      return res.data;
    },
    enabled: !!token,
    // Global retry policy: a bad/revoked token (401/403) never retries; an
    // unreachable backend retries up to the cap, then the outage page shows.
  });
}

export function useClientCalendarMonth(source: PortalSource) {
  const token = source.id;
  const live = useCalendarLive();
  return useQuery({
    queryKey: monthKey(token),
    queryFn: async () => {
      const res = await source.get<PortalMonth>("calendar");
      return scrubVendorTermsDeep(res.data);
    },
    enabled: !!token,
    // The team reworks declined posts, replies to comments, and re-sends the
    // month while this tab sits open — and another client's decision can close
    // the round. Slow poll; only a dead token (401/403) stops it — transient
    // errors keep polling so the page self-heals. While the brand is live the
    // calendar's realtime hints refresh it, and this is only the safety net.
    refetchInterval: (query) => (source.isDead(query.state.error) ? false : livePoll(live, POLL_PORTAL_MS)),
    refetchOnWindowFocus: !live,
  });
}

/** What a client comment carries besides its words (backend #128). */
export interface ClientCommentInput {
  body: string;
  /** The part of the page it is on; none for the whole post. */
  anchor?: { part: string; key?: string | null } | null;
  /** A reply's thread. */
  parentId?: string | null;
}

/** The request body: only the fields that are set, so a plain comment reads as before. */
export function commentBody(input: ClientCommentInput): Record<string, unknown> {
  const out: Record<string, unknown> = { body: input.body };
  if (input.anchor && !input.parentId) out.anchor = input.anchor;
  if (input.parentId) out.parentId = input.parentId;
  return out;
}

/**
 * Mark a thread done or open it again. The page shows the change at once
 * (useClientComments keeps it until the month's next read agrees).
 */
export function useClientCommentOp(source: PortalSource) {
  const token = source.id;
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { surface: "concept" | "brief"; commentId: string; op: "resolve" | "reopen" }) => {
      await source.post(`/comments/${payload.surface}/${payload.commentId}/${payload.op}`, {});
    },
    onSettled: (_data, _error, payload) => {
      void qc.invalidateQueries({
        queryKey: payload.surface === "concept" ? monthKey(token) : ["clientPortal", "briefs", token],
      });
    },
  });
}

/**
 * `optimistic: false` is for a page that shows the post itself while it goes
 * out and keeps it, with Try again, when it fails (the review page's comments
 * rail); the cache then only takes the server's copy.
 */
export function useAddClientComment(source: PortalSource, options: { optimistic?: boolean } = {}) {
  const token = source.id;
  const qc = useQueryClient();
  const optimistic = options.optimistic ?? true;
  return useMutation({
    mutationFn: async (payload: { versionId: string } & ClientCommentInput) => {
      const res = await source.post<{ comment: PortalComment }>(`/concept-versions/${payload.versionId}/comments`, commentBody(payload));
      return res.data.comment;
    },
    // Optimistic append so the client sees their own note land immediately; the
    // thread is display-only, so there is nothing here for a round trip to
    // protect against the way there is for a decision.
    onMutate: async (payload) => {
      if (!optimistic) return { previous: undefined };
      await qc.cancelQueries({ queryKey: monthKey(token) });
      const previous = qc.getQueryData<PortalMonth>(monthKey(token));
      const context = qc.getQueryData<PortalContext>(contextKey(token));
      const authorName = context?.client.name || context?.client.email || "You";
      const optimisticComment: PortalComment = {
        id: `optimistic-${Date.now()}`,
        authorType: "CLIENT",
        authorName,
        body: payload.body,
        createdAt: new Date().toISOString(),
      };

      qc.setQueryData<PortalMonth>(monthKey(token), (month) => {
        if (!month) return month;
        return {
          ...month,
          slots: month.slots.map((slot) => ({
            ...slot,
            conceptVersions: slot.conceptVersions?.map((version) =>
              version.id === payload.versionId
                ? { ...version, comments: [...version.comments, optimisticComment] }
                : version,
            ),
            idea:
              slot.activeConceptVersionId === payload.versionId && slot.idea
                ? { ...slot.idea, comments: [...slot.idea.comments, optimisticComment] }
                : slot.idea,
          })),
        };
      });
      return { previous };
    },
    onError: (_err, _payload, context) => {
      if (context?.previous) qc.setQueryData(monthKey(token), context.previous);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: monthKey(token) });
    },
  });
}

/**
 * Approve or decline one post.
 *
 * Optimistic on the one thing known for certain the instant the request is
 * sent: THIS post now has an answer. What that answer means for the round —
 * does it close, does the month flip — stays server-decided; `onSuccess`
 * reconciles that part with a refetch. Guessing the round-closing side effects
 * is what used to keep this off; guessing the slot's own decision is not the
 * same risk, and leaving the client staring at a stale "your call" panel for a
 * whole round trip was the worse failure mode.
 */
export function useSubmitDecision(source: PortalSource) {
  const token = source.id;
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      slotId: string;
      decision: PortalReviewDecision;
      reason?: string;
      conceptVersionId?: string;
    }) => {
      const res = await source.post<PortalDecisionResult>(`/slots/${payload.slotId}/decision`, {
          decision: payload.decision,
          reason: payload.reason ?? null,
          conceptVersionId: payload.conceptVersionId,
        });
      return res.data;
    },
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: monthKey(token) });
      const previous = qc.getQueryData<PortalMonth>(monthKey(token));
      const context = qc.getQueryData<PortalContext>(contextKey(token));
      const clientName = context?.client.name || context?.client.email || "You";
      const wasUndecided = previous?.slots.find((s) => s.id === payload.slotId)?.phase ===
        "awaiting_you";

      qc.setQueryData<PortalMonth>(monthKey(token), (month) => {
        if (!month) return month;
        return {
          ...month,
          slots: month.slots.map((s) =>
            s.id === payload.slotId
              ? {
                  ...s,
                  phase: "decided" as const,
                  conceptVersions: s.conceptVersions?.map((version) =>
                    version.id === (payload.conceptVersionId ?? s.activeConceptVersionId)
                      ? {
                          ...version,
                          readOnly: true,
                          decision: {
                            decision: payload.decision,
                            reason: payload.reason ?? null,
                            decidedAt: new Date().toISOString(),
                            clientName,
                          },
                        }
                      : version,
                  ),
                  decision: {
                    decision: payload.decision,
                    reason: payload.reason ?? null,
                    decidedAt: new Date().toISOString(),
                    clientName,
                    carriedOver: false,
                    mine: true,
                  },
                }
              : s,
          ),
          review:
            month.review && wasUndecided
              ? {
                  ...month.review,
                  decided: month.review.decided + 1,
                  awaiting: Math.max(0, month.review.awaiting - 1),
                }
              : month.review,
        };
      });
      // One answer per post: the brief that went out with the idea is decided
      // by the same click.
      await qc.cancelQueries({ queryKey: clientBriefsKey(token) });
      const previousBriefs = markBriefDecided(
        qc,
        token,
        { slotId: payload.slotId, decision: payload.decision, reason: payload.reason ?? null, clientName },
        "awaiting",
      );
      return { previous, previousBriefs };
    },
    onError: (_err, _payload, context) => {
      if (context?.previous) qc.setQueryData(monthKey(token), context.previous);
      if (context?.previousBriefs) qc.setQueryData(clientBriefsKey(token), context.previousBriefs);
      // A refused decision usually means the round moved under us (closed by
      // another client, recalled by the team) — fetch the truth right away
      // rather than waiting out the slow poll.
      qc.invalidateQueries({ queryKey: monthKey(token) });
      qc.invalidateQueries({ queryKey: clientBriefsKey(token) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: monthKey(token) });
      qc.invalidateQueries({ queryKey: clientBriefsKey(token) });
      qc.invalidateQueries({ queryKey: contextKey(token) });
    },
  });
}
